/**
 * Optimization Targets Mock
 *
 * Mirrors the optimization target selector from the platform,
 * showing the five available metrics with descriptions.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faPercent, faScaleBalanced, faBullseye, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface Target {
  label: string;
  description: string;
  icon: IconDefinition;
  color: string;
  selected?: boolean;
}

const targets: Target[] = [
  { label: 'Sharpe Ratio', description: 'Risk-adjusted returns', icon: faChartLine, color: '#3b82f6', selected: true },
  { label: 'Total Return', description: 'Maximize overall returns', icon: faPercent, color: '#22c55e' },
  { label: 'Profit Factor', description: 'Gross profit / gross loss ratio', icon: faScaleBalanced, color: '#f59e0b' },
  { label: 'Win Rate', description: 'Percentage of winning trades', icon: faBullseye, color: '#06b6d4' },
  { label: 'Max Drawdown', description: 'Minimize maximum loss from peak', icon: faShieldHalved, color: '#ef4444' },
];

export default function OptimizationTargetsMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Optimization Target
      </div>
      <div className="platform-mock-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {targets.map((t) => (
            <div key={t.label} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.625rem',
              padding: '0.5rem 0.625rem',
              background: t.selected ? `${t.color}15` : '#0c1520',
              border: `1px solid ${t.selected ? `${t.color}55` : 'rgba(255,255,255,0.06)'}`,
              borderRadius: '0.375rem',
              cursor: 'default',
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%',
                border: `2px solid ${t.selected ? t.color : '#4b5563'}`,
                background: t.selected ? `radial-gradient(circle, ${t.color} 40%, transparent 45%)` : 'transparent',
                flexShrink: 0,
              }} />
              <FontAwesomeIcon icon={t.icon} style={{ color: t.selected ? t.color : '#6b7280', fontSize: '0.625rem', width: 14 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.6875rem', color: t.selected ? '#e5e7eb' : '#9ca3af', fontWeight: t.selected ? 600 : 400 }}>
                  {t.label}
                </div>
                <div style={{ fontSize: '0.5rem', color: '#6b7280' }}>
                  {t.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="platform-mock-caption">
        Choose what to optimise for. Sharpe ratio balances return and risk; other targets pull parameters in different directions.
      </div>
    </div>
  );
}
